import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { useAuth } from "../lib/auth.js";
import { Layout } from "../components/Layout.js";
import { Login } from "../features/Login.js";
import { LoginEmailLink } from "../features/LoginEmailLink.js";
import { MotdArchive } from "../features/motd/MotdArchive.js";
import { Home } from "../features/Home.js";
import { PreprintsPortal } from "../features/preprints/PreprintsPortal.js";
import { PreprintDetails } from "../features/preprints/PreprintDetails.js";
import { ReviewersPortal } from "../features/reviewers/ReviewersPortal.js";
import { ArticleReviewers } from "../features/coi/ArticleReviewers.js";
import { QualityCheckPortal } from "../features/quality-check/QualityCheckPortal.js";
import { OperationsPortal } from "../features/operations/OperationsPortal.js";
import { ProductivityPortal } from "../features/productivity/ProductivityPortal.js";
import { MyProductivityRoute } from "../features/productivity/MyProductivityRoute.js";
import { StudentProductivity } from "../features/productivity/StudentProductivity.js";
import { PreprintProductivity } from "../features/productivity/PreprintProductivity.js";
import { AdminDashboard } from "../features/admin/AdminDashboard.js";
import { Spinner } from "../components/ui.js";
import { track } from "../lib/telemetry.js";

function isPublicPath(pathname: string): boolean {
  return (
    pathname === "/login" ||
    pathname === "/login/email-link" ||
    pathname === "/motd" ||
    pathname.startsWith("/motd/")
  );
}

function PageViewBeacon(): null {
  const location = useLocation();
  useEffect(() => {
    track("ui.page_view", { path: location.pathname });
  }, [location.pathname]);
  return null;
}

export function App(): JSX.Element {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (location.pathname === "/login/email-link") {
    return <LoginEmailLink />;
  }

  // Stay on the login form while /auth/me loads or refetches. Swapping in a
  // full-screen spinner remounts <Login /> and wipes the reject banner.
  if (loading && location.pathname !== "/login") {
    return (
      <div className="flex h-screen items-center justify-center">
        <Spinner label="Loading…" />
      </div>
    );
  }

  if (!user) {
    if (!isPublicPath(location.pathname)) {
      return <Navigate to="/login" replace />;
    }
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/login/email-link" element={<LoginEmailLink />} />
        <Route path="/motd" element={<MotdArchive />} />
        <Route path="/motd/*" element={<MotdArchive />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  if (location.pathname === "/login") {
    return <Navigate to="/" replace />;
  }

  // MOTD archive stays outside Layout so login ↔ archive feel continuous.
  if (location.pathname === "/motd" || location.pathname.startsWith("/motd/")) {
    return <MotdArchive />;
  }

  return (
    <Layout>
      <PageViewBeacon />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/preprints" element={<PreprintsPortal />} />
        <Route path="/preprints/:id" element={<PreprintDetails />} />
        {/* WO B3: article-details reviewer/COI surface — feature-flagged (redirects home when
            /api/health features.coi.enabled is false). NOTE: :id here is the PREPRINT id. */}
        <Route path="/preprints/:id/reviewers" element={<ArticleReviewers />} />
        <Route path="/reviewers" element={<ReviewersPortal />} />
        <Route path="/quality-check" element={<QualityCheckPortal />} />
        <Route path="/operations" element={<OperationsPortal />} />
        {/* WO-7/21 Track A: staff-only AE-productivity oversight (server-gated). */}
        <Route path="/productivity" element={<ProductivityPortal />} />
        {/* Productivity Tab v3: staff Team / Student productivity roster (server-gated). */}
        <Route path="/productivity/students" element={<StudentProductivity />} />
        {/* WO-2026-09-14: every pipeline preprint grouped by stage, with card actions (server-gated). */}
        <Route path="/productivity/preprints" element={<PreprintProductivity />} />
        {/* Self-scoped productivity, role-switched: AE Productivity v2 for AE-and-above,
            the student page (badges + funnels) for everyone else. */}
        <Route path="/my-productivity" element={<MyProductivityRoute />} />
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}
